import type { ReactNode } from 'react'
import { Button } from '@workflow/ui'
import { navigate, type Route } from '@/app/router'
import { setCookieChoice, useCookieChoice } from './cookies'

/**
 * The three internal pages named in the door's footnote: privacy, terms,
 * cookies. Short and honest, because the room is small — an invitation-only
 * studio console whose assets it serves itself. No contact page: there is no
 * public to contact us; members ask their administrator.
 */
function LegalFrame({ title, updated, children }: { title: string; updated: string; children: ReactNode }) {
  return (
    <div className="min-h-dvh bg-surface-sunken">
      <div className="mx-auto w-full max-w-2xl px-5 pt-16 pb-20 sm:px-8 sm:pt-24">
        <p className="text-micro text-muted-foreground uppercase">Workflow X TLS Studio</p>
        <h1 className="wf-display mt-3 text-foreground">{title}</h1>
        <p className="mt-2 text-caption text-muted-foreground">Last updated {updated}</p>
        <div className="mt-8 flex flex-col gap-4 text-ui leading-relaxed text-muted-foreground [&_h2]:pt-4 [&_h2]:text-ui [&_h2]:font-medium [&_h2]:text-foreground [&_p]:max-w-[68ch]">
          {children}
        </div>
        <div className="mt-10 flex flex-wrap gap-2">
          <Button size="sm" variant="ghost" onClick={() => navigate({ name: 'privacy' })}>Privacy</Button>
          <Button size="sm" variant="ghost" onClick={() => navigate({ name: 'terms' })}>Terms</Button>
          <Button size="sm" variant="ghost" onClick={() => navigate({ name: 'cookies' })}>Cookies</Button>
          <Button size="sm" variant="primary" onClick={() => navigate({ name: 'home' })}>
            Back to the door
          </Button>
        </div>
      </div>
    </div>
  )
}

const UPDATED = 'October 2026'

export function PrivacyPage() {
  return (
    <LegalFrame title="Privacy policy" updated={UPDATED}>
      <h2>What the studio holds</h2>
      <p>
        Your account (name, address, role), the profile you submit, and the work records
        you touch — jobs, notes, payments, availability. Nothing else is collected. There is
        no telemetry, no advertising, and no cross-site tracking of our own.
      </p>
      <h2>Where it lives</h2>
      <p>
        In the studio's own database, served through Cloudflare, which sees requests the way
        any host does. Fonts, images and every other asset are served by the studio itself —
        nothing on these pages loads from a third party except Google's sign-in, below.
      </p>
      <h2>Google sign-in</h2>
      <p>
        Signing in with Google proves your address to us; Google shares your name, address
        and profile photo, which the studio keeps on your account. Google's account chooser
        and One Tap prompt load only if you accept optional cookies.
      </p>
      <h2>Who sees it</h2>
      <p>
        Administrators see member records to run the studio. Players see only their own work
        and what the studio addresses to them. Nothing is sold or shared outward. Members ask
        their administrator about correction or removal.
      </p>
    </LegalFrame>
  )
}

export function TermsPage() {
  return (
    <LegalFrame title="Terms of service" updated={UPDATED}>
      <h2>An internal room</h2>
      <p>
        This console runs one studio's work. Access is by invitation only, and an
        administrator may change or end any account to protect the studio.
      </p>
      <h2>Your part</h2>
      <p>
        Use the access you were given, keep sign-in to yourself, and record hours and
        payments truthfully. The audit trail remembers every change; voiding, not deleting,
        is how mistakes are undone.
      </p>
      <h2>Availability</h2>
      <p>
        The studio runs this service as-is and keeps it best-effort. Features may change as
        the work requires, with the audit trail saying what moved.
      </p>
    </LegalFrame>
  )
}

export function CookiesPage() {
  const choice = useCookieChoice()
  return (
    <LegalFrame title="Cookie policy" updated={UPDATED}>
      <h2>Necessary — always on</h2>
      <p>
        The session cookie that keeps you signed in, and Cloudflare's security cookies in
        front of it. The door cannot work without these.
      </p>
      <h2>Optional — your call</h2>
      <p>
        Google's sign-in layer: the account chooser and the One Tap prompt. Refusing it
        changes nothing except that Google's prompt never loads — the Continue button still
        signs you in when you press it. The studio sets no other third-party cookies.
      </p>
      <h2>Preferences, not cookies</h2>
      <p>
        Theme, text size, ambient light and your cookie choice itself live in this
        browser's local storage, never in a cookie and never sent anywhere.
      </p>
      <h2>Your current choice</h2>
      <p>{choice === null ? 'Unanswered — nothing optional has loaded.' : choice.optional ? 'Accept all — Google\u2019s sign-in layer loads.' : 'Essentials only — Google\u2019s layer stays out.'}</p>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant={choice?.optional === true ? 'primary' : 'secondary'} onClick={() => setCookieChoice({ optional: true })}>
          Accept all
        </Button>
        <Button size="sm" variant={choice?.optional === false ? 'primary' : 'secondary'} onClick={() => setCookieChoice({ optional: false })}>
          Essentials only
        </Button>
      </div>
    </LegalFrame>
  )
}

/** The legal route behind the name, for the gate. */
export function LegalPage({ name }: { name: Extract<Route['name'], 'privacy' | 'terms' | 'cookies'> }) {
  if (name === 'terms') return <TermsPage />
  if (name === 'cookies') return <CookiesPage />
  return <PrivacyPage />
}
